import './HelpPage.css';

function HelpPage() {
    return (
        <div className="help-page">
            <div className="help-header">
                <h1>Looper MIDI Control</h1>
                <p className="help-subtitle">User Guide & Documentation</p>
            </div>

            <div className="help-content">
                {/* What is this app? */}
                <section className="help-section">
                    <h2>📱 What is this App?</h2>
                    <p>
                        Looper MIDI Control is a web-based MIDI controller interface for the <strong>Ed Sheeran Looper+</strong> hardware device.
                        It provides a visual, desktop-friendly way to control all looper functions via USB MIDI connection.
                    </p>
                    <div className="feature-grid">
                        <div className="feature-card">
                            <h3>🎛️ Desktop Controls</h3>
                            <p>Large, clickable buttons for recording, playback, muting, and track selection</p>
                        </div>
                        <div className="feature-card">
                            <h3>🎸 Hardware Visualization</h3>
                            <p>A visual model of your looper — tracks, LEDs, mode and program slot at a glance</p>
                        </div>
                        <div className="feature-card">
                            <h3>⚙️ Profile Management</h3>
                            <p>Save and switch between different MIDI configurations</p>
                        </div>
                        <div className="feature-card">
                            <h3>🗂️ Loop File Manager</h3>
                            <p>Organize your loop slots and track metadata</p>
                        </div>
                    </div>
                </section>

                {/* How to Connect */}
                <section className="help-section">
                    <h2>🔌 How to Connect</h2>
                    <div className="connection-steps">
                        <div className="step">
                            <div className="step-number">1</div>
                            <div className="step-content">
                                <h3>Connect USB Cable</h3>
                                <p>Plug your Ed Sheeran Looper+ into your computer using a USB cable. Make sure the looper is powered on.</p>
                            </div>
                        </div>
                        <div className="step">
                            <div className="step-number">2</div>
                            <div className="step-content">
                                <h3>Open MIDI Settings</h3>
                                <p>Click the <strong>Settings</strong> gear icon (⚙️) in the top right, then navigate to the <strong>MIDI</strong> tab.</p>
                            </div>
                        </div>
                        <div className="step">
                            <div className="step-number">3</div>
                            <div className="step-content">
                                <h3>Select USB Device</h3>
                                <p>In the dropdown menus, look for "USB Devices" group and select your Looper for both Input and Output.</p>
                            </div>
                        </div>
                        <div className="step">
                            <div className="step-number">4</div>
                            <div className="step-content">
                                <h3>Click Connect or Auto-Connect</h3>
                                <p>Click the <strong>Connect</strong> button or use <strong>Auto Connect</strong> for automatic device detection.
                                    The status badge will turn green (✅ Connected) when successful.</p>
                            </div>
                        </div>
                    </div>
                    <div className="alert alert-info">
                        <strong>Troubleshooting:</strong> If you don't see your device, ensure it's not in USB Storage Mode.
                        The Looper must be in MIDI mode for this app to work.
                    </div>
                </section>

                {/* Configuration Guide */}
                <section className="help-section">
                    <h2>⚙️ Configuration Guide</h2>
                    <p>Access configuration by clicking <strong>Settings → Config</strong>.</p>

                    <h3>Footswitch Mapping</h3>
                    <p>Customize what each footswitch (FS1, FS2, EXT1, EXT2) does:</p>
                    <ul>
                        <li><strong>FS1 (Left Main):</strong> Typically Record/Play for Track A</li>
                        <li><strong>FS2 (Right Main):</strong> Typically Stop/Mute function</li>
                        <li><strong>EXT1 & EXT2:</strong> Optional external footswitches - enable the checkbox to use them</li>
                    </ul>
                    <p>Each footswitch can be assigned to MIDI functions like Rec/Play, Stop, Mute, Undo/Redo, Speed, Reverse, etc.</p>

                    <h3>Encoder Settings</h3>
                    <p>The rotary encoder (knob) can be assigned to:</p>
                    <ul>
                        <li>Main Volume</li>
                        <li>Fade Time</li>
                        <li>Track A Volume</li>
                        <li>Track B Volume</li>
                        <li>Crossfade A/B</li>
                        <li>Playback Speed</li>
                        <li>Menu Navigation</li>
                    </ul>
                    <p className="text-sm text-secondary">
                        Note: the Looper+ manual does not document encoder CC numbers — these mappings are app assumptions.
                    </p>

                    <h3>MIDI Channel</h3>
                    <p>Set the MIDI channel (1-16) that your Looper is configured to receive on. Default is usually Channel 1.</p>

                    <h3>External Footswitches</h3>
                    <p>If you have external footswitches connected to your Looper+:</p>
                    <ul>
                        <li>Check the <strong>"Enabled"</strong> box for EXT1 and/or EXT2 in Config</li>
                        <li>They will appear in the hardware visualization when enabled</li>
                        <li>Assign their function from the dropdown menu</li>
                    </ul>
                </section>

                {/* Desktop Controls */}
                <section className="help-section">
                    <h2>🎛️ Desktop Controls</h2>
                    <p>The main control interface provides large, clickable buttons for all looper functions:</p>

                    <h3>Track A & B Controls</h3>
                    <ul>
                        <li><strong>REC / PLAY:</strong> Start recording, then toggle to playback, then overdub (cycle)</li>
                        <li><strong>SELECT:</strong> Make this track the active/selected track</li>
                        <li><strong>MUTE:</strong> Mute/unmute the track</li>
                    </ul>

                    <h3>Center Controls</h3>
                    <ul>
                        <li><strong>STOP ALL:</strong> Stop all tracks immediately</li>
                        <li><strong>REVERSE:</strong> Toggle reverse playback on the selected track</li>
                        <li><strong>SPEED:</strong> Toggle double-speed playback</li>
                    </ul>

                    <h3>Bottom Safety Bar</h3>
                    <ul>
                        <li><strong>UNDO / REDO:</strong> Undo or redo the last recording action</li>
                        <li><strong>CLEAR SESSION:</strong> Delete all recordings (hold for 1 second to confirm)</li>
                    </ul>
                </section>

                {/* Profiles */}
                <section className="help-section">
                    <h2>💾 Profile Management</h2>
                    <p>Profiles let you save different MIDI configurations for various setups or performances:</p>
                    <ul>
                        <li><strong>New:</strong> Create a fresh profile</li>
                        <li><strong>Save:</strong> Save changes (appears when modified)</li>
                        <li><strong>Delete:</strong> Remove the current profile</li>
                        <li><strong>Reset All:</strong> Delete all profiles and create a fresh default</li>
                    </ul>
                    <p>Your selected profile is automatically loaded when you return to the app.</p>
                </section>

                {/* Loop Files */}
                <section className="help-section">
                    <h2>🗂️ Loop File Manager</h2>
                    <p>Organize loop slots and track metadata for your Looper device.</p>

                    <div className="alert alert-warning">
                        <strong>⚠️ Important:</strong> This manager organizes loop <em>slots</em> only.
                        To transfer actual audio files to your Looper+:
                        <ol>
                            <li>Connect your Looper via USB in <strong>Storage Mode</strong></li>
                            <li>Drag audio files to the device's main folder</li>
                            <li>Disconnect and restart the Looper to index them</li>
                        </ol>
                        MIDI cannot transfer audio files directly.
                    </div>
                </section>

                {/* External Resources */}
                <section className="help-section">
                    <h2>📚 External Resources</h2>
                    <p>
                        For the authoritative note map and hardware behaviour, refer to the printed
                        <strong> Sheeran Looper+ User Guide</strong> that shipped with your device —
                        this app's mappings in <code>src/constants/functions.js</code> are locked to its §4.5.4 by a unit test.
                    </p>
                </section>
            </div>
        </div>
    );
}

export default HelpPage;
